import {
  DeleteOutlined,
  MenuOutlined,
  MergeCellsOutlined,
  PlusOutlined,
  RotateLeftOutlined
} from '@ant-design/icons'
import { ListViewFilter } from '@crm/api'
import {
  Button,
  Checkbox,
  Dropdown,
  Flex,
  List,
  Select,
  Space,
  Tag,
  Typography
} from 'antd'
import * as React from 'react'

import { clean, mergeElements, move } from './utils'
import { DndZone } from '../DndComponents'

type ListViewFilterWithTitle = ListViewFilter & {
  title?: string
}

type Props = {
  value?: string
  data: ListViewFilterWithTitle[]
  onChange?: (value: string) => void
}

export const InputLogic = ({ value: defaultValue, data, onChange }: Props) => {
  const [newOption, setNewOption] = React.useState('AND')
  const [value, setValue] = React.useState<string[]>(
    defaultValue ? defaultValue?.split(' ') : []
  )
  const [selected, setSelected] = React.useState<number[]>([])
  const [loaded, setLoaded] = React.useState(false)

  React.useEffect(() => {
    if (!loaded && defaultValue && value.join(' ') !== defaultValue) {
      setValue(defaultValue.split(' '))
      setLoaded(true)
    }
  }, [defaultValue, loaded, value])

  React.useEffect(() => {
    if (onChange) {
      onChange(value.join(' '))
    }
  }, [value, onChange])

  const onSetSelected = (index: number, checked: boolean) => {
    setSelected((prevState) =>
      checked ? prevState.filter((i) => i !== index) : [...prevState, index]
    )
  }

  const SortableItem = ({
    value: text,
    index
  }: {
    value: string
    index: number
  }) => {
    const checked = selected.includes(index)

    return (
      <List.Item>
        <DndZone.ZoneItem id={index} styleOverlay>
          {({ listeners, setActivatorNodeRef }) => (
            <Space.Compact block>
              <Button type={checked ? 'primary' : 'default'}>
                <Flex>
                  <MenuOutlined ref={setActivatorNodeRef} {...listeners} />
                </Flex>
              </Button>
              <Button disabled ghost>
                <Typography.Text>
                  {data[Number.parseInt(text) - 1]?.title || '---'}
                </Typography.Text>
              </Button>

              <Button onClick={() => onSetSelected(index, checked)}>
                <Checkbox
                  indeterminate={!checked}
                  checked={checked}
                  onChange={() => onSetSelected(index, checked)}
                />
              </Button>

              <Button
                icon={<DeleteOutlined />}
                onClick={() => {
                  setValue((prevState) => {
                    const index = prevState.findIndex((val) => val === text)

                    if (!index) {
                      return prevState.slice(2)
                    } else if (index === prevState.length - 1) {
                      return prevState.slice(0, -2)
                    } else {
                      return prevState.filter(
                        (_, i) => i !== index && i !== index + 1
                      )
                    }
                  })
                }}
              />
            </Space.Compact>
          )}
        </DndZone.ZoneItem>
      </List.Item>
    )
  }

  const SortableList = ({ items }: { items?: string[] }) =>
    items?.length ? (
      <List
        dataSource={items}
        grid={{ gutter: 24 }}
        renderItem={(item, i) =>
          Number.parseInt(item) ? (
            <SortableItem key={i} index={i} value={item} />
          ) : ['AND', 'OR'].includes(item) ? (
            <Space key={`item-${item}`} align="center">
              <Dropdown
                placement="topCenter"
                menu={{
                  items: [
                    { key: 'AND', label: ' AND' },
                    { key: 'OR', label: ' OR' }
                  ],
                  onClick: ({ key }) =>
                    setValue((prevState) =>
                      prevState.map((prevValue, index) =>
                        item && index === i ? key : prevValue
                      )
                    )
                }}
              >
                <Tag bordered={false}>{item}</Tag>
              </Dropdown>

              {selected?.includes(i - 1) && selected?.includes(i + 1) ? (
                <Button
                  icon={<MergeCellsOutlined />}
                  onClick={() =>
                    setValue((prevState) => {
                      setSelected((prevState) =>
                        prevState.filter(
                          (_, index) => index !== i - 1 && index !== i + 1
                        )
                      )

                      return mergeElements({ elements: prevState, i })
                    })
                  }
                />
              ) : items[i - 2] === '(' || items[i + 2] === ')' ? (
                <Button
                  icon={<RotateLeftOutlined />}
                  onClick={() =>
                    setValue((prevState) => {
                      setSelected((prevState) => [
                        ...prevState,
                        i - 2 || 0,
                        i + 2
                      ])

                      return mergeElements({ elements: prevState, i }, true)
                    })
                  }
                />
              ) : null}
            </Space>
          ) : (
            <Space>
              {item}
              {(items[i - 1] === '(' || items[i + 1] === ')') && (
                <Button
                  icon={<RotateLeftOutlined />}
                  onClick={() =>
                    setValue((prevState) => {
                      const startIndex = i - 2

                      const newValue = prevState

                      newValue?.splice(startIndex, 1)

                      let isFinded = 0
                      newValue?.forEach((val, index) => {
                        if (index >= startIndex && !isFinded && val === ')') {
                          isFinded = index
                        }
                      })

                      return newValue?.filter(
                        (_, index) => !isFinded || isFinded !== index
                      )
                    })
                  }
                />
              )}
            </Space>
          )
        }
      />
    ) : null

  return (
    <Space align="start">
      {value?.length ? (
        <DndZone
          items={value}
          getSortableItem={(_, i) => i}
          onDragEnd={(_, { active, over }) => {
            setValue((prevState) =>
              move(prevState, +active.id, +(over?.id || 0))
            )
          }}
        >
          <SortableList items={value} />
        </DndZone>
      ) : null}

      {!!data?.length && (
        <Space>
          <Select
            value={newOption}
            options={[
              { value: 'AND', text: ' AND' },
              { value: 'OR', text: ' OR' }
            ]}
            onChange={(val) => setNewOption(val)}
          />

          <Dropdown
            placement="bottomLeft"
            menu={{
              items: data?.map((val: ListViewFilterWithTitle, i: number) => ({
                key: i + 1,
                label: val.title || '---'
              })),
              onClick: ({ key }) =>
                setValue((prevState) =>
                  clean([...prevState, newOption, key?.toString()])
                )
            }}
          >
            <Button icon={<PlusOutlined />} />
          </Dropdown>
        </Space>
      )}
    </Space>
  )
}
